import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StartIcon from "@mui/icons-material/Start";
import { Box, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  APPOINTMENT_CHANGE_STATUS,
  AppointmentStatusChangeModal,
} from "@/components/appointment-status-change";
import Flex from "../../../components/Flex";
import Text from "../../../components/Text";
import { appPaths } from "../../../constants/paths";
import { useReduceDisclosure } from "../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../hooks/useSnackbar";
import { useUpdateAppointmentStatus } from "../../../queries/use-change-appointment-status";
import {
  closeAndResetJoinRoom,
  minimize,
} from "../../../redux/feature/join-room/JoinRoomSlice";
import { RootState, store } from "../../../redux/store";
import { formatDateTime } from "../../../utils/date-time-utility";
import {
  getErrorSnackbar,
  getPatientVisitNoteUrl,
  getXtenantId,
} from "../../../utils/utils";
import { NoShowConfirmation } from "./NoShow";
import StyledButtonV2 from "../../../components/app/StyledButtonV2";
import { useQueryClient } from "@tanstack/react-query";
import { getStoredRealm } from "@/utils/auth-storage";

const ZoomStartEncounterBar = ({ zmClient }: { zmClient?: any }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();
  const callData: any = useSelector((state: RootState) => state.joinRoom);
  const [cancelledAppointmentDisclosure, noShowDisclosure] =
    useReduceDisclosure(2);
  const showSnackbar = useSnackbar();

  const handleNavigateStartEncounter = () => {
    updateStatus({
      appointmentId: store.getState().joinRoom.appointmentId.toString()!,
      status: "IN_EXAM",
      xTENANTID: getStoredRealm() ?? undefined,
    });

    store.dispatch(minimize());

    // navigate(
    //   `${appPaths.ENCOUNTER}?patientid=${
    //     store.getState().joinRoom.patientId
    //   }&appointmentid=${store.getState().joinRoom.appointmentId}`
    // );

    navigate(
      getPatientVisitNoteUrl(
        store.getState().joinRoom.patientId,
        store.getState().joinRoom.appointmentId
      )
    );
  };

  async function onLeaveClick() {
    await zmClient?.leave().then(() => {
      store.dispatch(closeAndResetJoinRoom());
    });
    if (
      !store.getState().joinRoom.isMinimize &&
      location.pathname === "/app/preview"
    ) {
      navigate(-1);
    }
    queryClient.invalidateQueries({
      queryKey: ["appointment-list-v2"],
    });
  }

  const handleStatusChange = (status: string) => {
    return updateStatus({
      appointmentId: callData?.appointmentId?.toString() ?? "",
      status: status as any,
      xTENANTID: getXtenantId(),
    })
      .then(() => onLeaveClick())
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  return (
    <Box
      bgcolor={"white"}
      position={"absolute"}
      width={"100%"}
      height={"70px"}
      top={0}
      //   zIndex={99}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"}>
        <Flex gap={1}>
          <ArrowBackIcon
            sx={{
              marginTop: 0.4,
              cursor: "pointer",
            }}
            fontSize="medium"
            onClick={async () => {
              await zmClient?.leave().then(() => {
                store.dispatch(closeAndResetJoinRoom());
              });

              navigate(appPaths.schedulingAppointment);
            }}
          />
          <Box>
            <Text fontSize={"20px"}>
              {callData?.patientName} Appointment With Provider{" "}
              {callData?.providerName}
            </Text>
            <Text fontSize={"18px"}>
              {formatDateTime(
                callData?.startTime?.replace(/\.\d{4,}Z$/, ".000Z")
              )}
            </Text>
          </Box>
        </Flex>

        <AppointmentStatusChangeModal
          disclosureHost={cancelledAppointmentDisclosure}
          appointmentId={callData?.appointmentId?.toString()}
          status={APPOINTMENT_CHANGE_STATUS.CANCELLED}
          onSuccess={onLeaveClick}
        />

        <Flex gap={1}>
          <StyledButtonV2
            onClick={() => noShowDisclosure.open()}
            variant={"outlined"}
            sx={{
              backgroundColor: "info.light",
            }}
            label={"No Show"}
          />

          <NoShowConfirmation
            disclosureHost={noShowDisclosure}
            onCancel={() => {
              noShowDisclosure.close();
            }}
            onConfirm={() => handleStatusChange("NO_SHOW")}
          />

          <StyledButtonV2
            onClick={() => cancelledAppointmentDisclosure.open()}
            variant={"outlined"}
            sx={{
              backgroundColor: "error.main",
              color: "white",
              "&:hover": {
                color: "black",
              },
            }}
            label={"Cancel"}
          />
          <Button
            onClick={handleNavigateStartEncounter}
            startIcon={<StartIcon />}
            variant={"contained"}
          >
            Start Encounter
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default ZoomStartEncounterBar;
